import React, { useState } from 'react';
import { Mail, Lock, Eye, EyeOff, XCircle, User, Check } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AuthSubmitButton } from './AuthSubmitButton';
import { FieldError } from '@/components/ui/FieldError';
import { useInlineValidation } from '@/hooks/useInlineValidation';

interface SignUpFormProps {
    onSubmit: (email: string, password: string, name: string, consentC: boolean) => Promise<void>;
    loading: boolean;
    error: string | null;
    onConflict: (email: string, existingProvider: 'google' | 'email') => void;
    onSwitchToLogin: () => void;
}

export const SignUpForm: React.FC<SignUpFormProps> = ({
    onSubmit,
    loading,
    error,
    onConflict,
    onSwitchToLogin
}) => {
    const t = useTranslations('auth.signup');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [name, setName] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [consentC, setConsentC] = useState(false);

    // Both rules used to fire only on submit, so someone typed a password, typed
    // a confirmation that did not match, filled in the consent box, pressed the
    // button and only then learned. They are told on blur now, and the message
    // clears on the keystroke that fixes it rather than 400ms later.
    const passwordCheck = useInlineValidation({
        value: password,
        validate: (v) => (v.length > 0 && v.length < 8 ? t('passwordTooShort') : null),
    });
    const confirmCheck = useInlineValidation({
        value: confirmPassword,
        // Reads the other field, so it re-runs when the other field changes.
        revalidateOn: password,
        validate: (v) => (v.length > 0 && v !== password ? t('passwordMismatch') : null),
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setValidationError(null);

        // commit() on submit as well as on blur. The source hook fires only on
        // blur, which is how a form completed and submitted from the keyboard,
        // never blurring the last field, shows no error at all.
        const passwordOk = passwordCheck.commit();
        const confirmOk = confirmCheck.commit();
        if (!passwordOk || !confirmOk) return;

        if (password !== confirmPassword) {
            setValidationError(t('passwordMismatch'));
            return;
        }

        if (!consentC) {
            setValidationError(t('consentRequired'));
            return;
        }

        await onSubmit(email, password, name, consentC);
    };

    const displayError = validationError || error;

    return (
        <>
            <div className="mb-6 text-center">
                <h2 className="text-sm font-semibold text-fg mb-2">{t('title')}</h2>
                <p className="text-sm text-fg-secondary leading-relaxed max-w-[280px] mx-auto">{t('description')}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">

                {/* Every field's accessible name is its visible label. There are no
                    placeholders, so a name can never collapse into one. */}
                <div className="space-y-2 animate-in slide-in-from-top-2 fade-in duration-300">
                    <label htmlFor="signup-name" className="block text-sm text-fg-secondary">
                        {t('fullName')}
                    </label>
                    <div className="relative group/input">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center justify-center pointer-events-none z-10 h-4 w-4">
                            <User className="w-full h-full text-fg-muted group-focus-within/input:text-fg transition-colors" />
                        </div>
                        <Input
                            id="signup-name"
                            type="text"
                            autoComplete="name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            className="h-11 pl-11"
                        />
                    </div>
                </div>

                <div className="space-y-2">
                    <label htmlFor="signup-email" className="block text-sm text-fg-secondary">
                        {t('email')}
                    </label>
                    <div className="relative group/input">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center justify-center pointer-events-none z-10 h-4 w-4">
                            <Mail className="w-full h-full text-fg-muted group-focus-within/input:text-fg transition-colors" />
                        </div>
                        <Input
                            id="signup-email"
                            type="email"
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="h-11 pl-11"
                        />
                    </div>
                </div>

                <div className="space-y-2">
                    <label htmlFor="signup-password" className="block text-sm text-fg-secondary">
                        {t('password')}
                    </label>
                    <div className="relative group/input">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center justify-center pointer-events-none z-10 h-4 w-4">
                            <Lock className="w-full h-full text-fg-muted group-focus-within/input:text-fg transition-colors" />
                        </div>
                        <Input
                            id="signup-password"
                            type={showPassword ? "text" : "password"}
                            autoComplete="new-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            {...passwordCheck.fieldProps}
                            aria-describedby={passwordCheck.error ? 'signup-password-error' : undefined}
                            className="h-11 pl-11 pr-11"
                        />
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => setShowPassword(!showPassword)}
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                            aria-pressed={showPassword}
                            className="absolute right-1 top-1/2 -translate-y-1/2 z-20 h-8 w-8 text-fg-muted hover:text-fg"
                        >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                    </div>
                    <FieldError
                        id="signup-password-error"
                        message={passwordCheck.error}
                        className="text-[11px] text-danger-text"
                    />
                </div>

                <div className="space-y-2 animate-in slide-in-from-top-2 fade-in duration-300">
                    <label htmlFor="signup-confirm-password" className="block text-sm text-fg-secondary">
                        {t('confirmPassword')}
                    </label>
                    <div className="relative group/input">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center justify-center pointer-events-none z-10 h-4 w-4">
                            <Lock className="w-full h-full text-fg-muted group-focus-within/input:text-fg transition-colors" />
                        </div>
                        <Input
                            id="signup-confirm-password"
                            type="password"
                            autoComplete="new-password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            {...confirmCheck.fieldProps}
                            aria-describedby={confirmCheck.error ? 'signup-confirm-error' : undefined}
                            className="h-11 pl-11"
                        />
                    </div>
                    <FieldError
                        id="signup-confirm-error"
                        message={confirmCheck.error}
                        className="text-[11px] text-danger-text"
                    />
                </div>

                {/* GDPR Art. 9(2)(a) explicit consent. Required for mood/habit/cycle data.
                    The box paints itself via appearance-none rather than hiding a native
                    control under an opacity-0 overlay, so the focus ring lands on the
                    element that actually receives focus. */}
                <div className="flex items-start gap-3 pt-1">
                    <span className="relative mt-1 shrink-0 inline-flex h-4 w-4 items-center justify-center">
                        <Input
                            id="signup-consent"
                            type="checkbox"
                            checked={consentC}
                            onChange={(e) => setConsentC(e.target.checked)}
                            className="h-4 w-4 appearance-none rounded-sm px-0 py-0 cursor-pointer hover:border-border-strong checked:bg-primary checked:border-primary"
                        />
                        {consentC && (
                            <Check
                                strokeWidth={3}
                                aria-hidden="true"
                                className="absolute w-2.5 h-2.5 text-primary-foreground pointer-events-none"
                            />
                        )}
                    </span>
                    <label htmlFor="signup-consent" className="text-[11px] leading-snug text-fg-secondary cursor-pointer">
                        {t.rich('consentLabel', {
                            strong: (chunks) => <span className="text-fg font-semibold">{chunks}</span>,
                        })}
                    </label>
                </div>

                {/* A validation or auth failure is announced, not just rendered. */}
                {displayError && (
                    <div role="alert" className="flex items-start gap-2 animate-in slide-in-from-top-2">
                        <span className="flex h-5 w-4 shrink-0 items-center">
                            <XCircle className="w-4 h-4 text-danger-text" />
                        </span>
                        <p className="text-sm text-danger-text leading-snug">{displayError}</p>
                    </div>
                )}

                <AuthSubmitButton
                    loading={loading}
                    disabled={!consentC}
                    className="mt-2 disabled:cursor-not-allowed"
                >
                    {t('submit')}
                </AuthSubmitButton>
            </form>

            <div className="mt-6 text-center">
                <Button
                    variant="ghost"
                    onClick={onSwitchToLogin}
                    className="text-[11px] font-semibold uppercase tracking-[0.09em]"
                >
                    {t('hasAccount')} {t('logIn')}
                </Button>
            </div>
        </>
    );
};
